import { Button, Chip, Stack } from "@mui/material";
import { FC, useMemo } from "react";
import { useListContext } from "react-admin";
import { useGetSubscriptionPlans } from "../../queries";
import { formatUserRoleToName, keepObjectWithKeys } from "@/utils";
import { UsersListFilter } from "../../types";
import { UserStatus } from "@/types";

//TODO: refactor displaying filters maybe with getDisplayFilters util func
const FiltersList: FC = () => {
  const { filterValues, setFilters } = useListContext();
  const { subscriptionPlans } = useGetSubscriptionPlans();

  const roleDisplayFilters = useMemo(() => {
    const currentRoleFilter = (filterValues as UsersListFilter).roles ?? [];

    const displayFilters = currentRoleFilter.map((role) => ({
      id: role,
      label: formatUserRoleToName(role),
      onDelete: () =>
        setFilters({
          ...filterValues,
          roles: currentRoleFilter.filter((r) => r !== role),
        }),
    }));

    return displayFilters;
  }, [filterValues.roles]);

  const statusesDisplayFilters = useMemo(() => {
    const currentStatusesFilter =
      (filterValues as UsersListFilter).statuses ?? [];

    const isActive = currentStatusesFilter.includes(UserStatus.ACTIVE);
    const isInactive =
      currentStatusesFilter.includes(UserStatus.NOT_FINISHED_PROFILE) &&
      currentStatusesFilter.includes(UserStatus.INACTIVE);

    const displayFilters = [];
    if (isActive) {
      displayFilters.push({
        id: "active",
        label: "Active",
        onDelete: () =>
          setFilters({
            ...filterValues,
            statuses: currentStatusesFilter.filter(
              (s) => s !== UserStatus.ACTIVE
            ),
          }),
      });
    }
    if (isInactive) {
      const inactiveStatuses = [
        UserStatus.NOT_FINISHED_PROFILE,
        UserStatus.INACTIVE,
      ];

      displayFilters.push({
        id: "inactive",
        label: "Inactive",
        onDelete: () =>
          setFilters({
            ...filterValues,
            statuses: currentStatusesFilter.filter(
              (s) => !inactiveStatuses.includes(s)
            ),
          }),
      });
    }

    return displayFilters;
  }, [filterValues.statuses]);

  const subscriptionTiersDisplayFilters = useMemo(() => {
    const currentTiersFilter = (filterValues as UsersListFilter).planIds ?? [];

    const displayFilters = currentTiersFilter.map((tier) => ({
      id: tier,
      label:
        subscriptionPlans?.find(({ id }) => id === tier)?.title ||
        "Untitled tier",
      onDelete: () =>
        setFilters({
          ...filterValues,
          planIds: currentTiersFilter.filter((t) => t !== tier),
        }),
    }));

    return displayFilters;
  }, [filterValues.planIds, subscriptionPlans]);

  const displayFilters = useMemo(
    () => [
      ...roleDisplayFilters,
      ...statusesDisplayFilters,
      ...subscriptionTiersDisplayFilters,
    ],
    [
      roleDisplayFilters,
      statusesDisplayFilters,
      subscriptionTiersDisplayFilters,
    ]
  );

  return (
    <Stack direction={"row"} spacing={0.5} alignItems={"center"}>
      {displayFilters.map(({ id, label, onDelete }) => (
        <Chip
          key={id}
          size="small"
          variant="filled"
          label={label}
          onClick={onDelete}
          onDelete={onDelete}
        />
      ))}
      {!!Object.values(
        keepObjectWithKeys(filterValues as UsersListFilter, [
          "statuses",
          "roles",
          "planIds",
        ])
      ).length && (
        <Button
          size="small"
          variant="outlined"
          onClick={() => setFilters({}, [])}
        >
          Clear All
        </Button>
      )}
    </Stack>
  );
};

export { FiltersList };
